import { Box, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import SentraConnectForm from "./SentraConnectForm";

const SentraIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("sentra");
  const [loading, setLoading] = useState(false);
  const { registerSentraIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    registerSentraIntegration(values.api_token)
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Sentra integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated && !loading && !isIntegratedLoading
            ? "Once integrated, the Breeze platform will retrieve information about sensitive data assets from Sentra."
            : ""
        }
        image={logos.SentraLogo}
        name={"Sentra"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>Sentra is integrated!</Typography>
            </Box>
          ) : (
            <div>
              {guideMode && <IntegrationGuideAlert />}

              <GuidelineStepByStep>
                <GuidelineStep title="Login to Sentra">
                  <Typography variant={"body2"}>
                    Login to your Sentra account at{" "}
                    <a
                      href="https://app.sentra.io"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      https://app.sentra.io
                    </a>
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Create a new API token">
                  <Typography variant={"body2"}>
                    Navigate to <b>Settings</b> {">"} <b>API Tokens</b> and
                    create a new API token with the necessary permissions.
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Paste the API token here">
                  <Box width={700}>
                    <SentraConnectForm
                      onSubmit={onSubmit}
                      guideMode={guideMode}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default SentraIntegration;
